// packages/api/src/hooks/admin/usePilar2Workflow.ts
//
// ESCR-206.6 — Hooks de workflow SD-1 do Pilar 2 (28 endpoints: 7 ops × 4
// entidades — products, variants, plans, plan-products). Substitui uma
// factory de 21 hooks individuais (recomendação do contrato ESCR-206.3) por
// um único `usePilar2Workflow(entityType)` que retorna 6 mutations.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ApiSuccess } from '@alamo/types';

import { apiClient } from '../../client';

import { pricingTablesKeys } from './usePricingTables';

/** Mapeia o entityType para o path da API. */
export type Pilar2WorkflowEntity = 'product' | 'product_variant' | 'plan' | 'plan_product';

const ENTITY_TO_PATH: Record<Pilar2WorkflowEntity, string> = {
  product: 'products',
  product_variant: 'variants',
  plan: 'plans',
  plan_product: 'plan-products',
};

const BASE = '/admin/pricing-tables';

export type Pilar2EntityStatus =
  | 'draft'
  | 'approval_pending'
  | 'published'
  | 'rejected'
  | 'archived';

export type Pilar2WorkflowOperation =
  | 'submit'
  | 'approve'
  | 'reject'
  | 'withdraw'
  | 'publish'
  | 'archive';

export interface Pilar2HistoryEntry {
  id: string;
  entityType: Pilar2WorkflowEntity;
  entityId: string;
  operation: Pilar2WorkflowOperation | 'unarchive';
  previousStatus: Pilar2EntityStatus | null;
  newStatus: Pilar2EntityStatus | null;
  actorUserId: string;
  notes: string | null;
  createdAt: string;
}

/**
 * Cria as 6 mutations de transição para uma entidade do Pilar 2. Cada
 * mutation invalida o cache de listings/details ao concluir.
 *
 * Uso:
 *   const wf = usePilar2Workflow<PricingProduct>('product');
 *   wf.submit.mutate(productId);
 *   wf.reject.mutate({ id, reason: 'preço acima do teto' });
 */
export function usePilar2Workflow<TEntity = unknown>(entityType: Pilar2WorkflowEntity) {
  const qc = useQueryClient();
  const path = ENTITY_TO_PATH[entityType];
  const invalidate = () => qc.invalidateQueries({ queryKey: pricingTablesKeys.all });

  // 6 mutations chamadas em ordem fixa — seguro com as Rules of Hooks porque
  // `entityType` muda recria o hook tree externamente (cada `usePilar2Workflow`
  // call mapeia 1:1 com uma entidade no componente).
  const submit = useMutation<TEntity, unknown, string>({
    mutationFn: async (id) => {
      const res = await apiClient.post<ApiSuccess<TEntity>>(`${BASE}/${path}/${id}/submit`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  const approve = useMutation<TEntity, unknown, string>({
    mutationFn: async (id) => {
      const res = await apiClient.post<ApiSuccess<TEntity>>(`${BASE}/${path}/${id}/approve`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  const reject = useMutation<TEntity, unknown, { id: string; reason: string }>({
    mutationFn: async ({ id, reason }) => {
      const res = await apiClient.post<ApiSuccess<TEntity>>(`${BASE}/${path}/${id}/reject`, {
        reason,
      });
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  const withdraw = useMutation<TEntity, unknown, string>({
    mutationFn: async (id) => {
      const res = await apiClient.post<ApiSuccess<TEntity>>(`${BASE}/${path}/${id}/withdraw`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  const publish = useMutation<TEntity, unknown, string>({
    mutationFn: async (id) => {
      const res = await apiClient.post<ApiSuccess<TEntity>>(`${BASE}/${path}/${id}/publish`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  const archive = useMutation<TEntity, unknown, string>({
    mutationFn: async (id) => {
      const res = await apiClient.post<ApiSuccess<TEntity>>(`${BASE}/${path}/${id}/archive`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  return { submit, approve, reject, withdraw, publish, archive };
}

/** Hook conveniente para permissões de pricing (ESCR-206.8). */
export interface PricingPermissions {
  canManage: boolean;
  canApprove: boolean;
  canPublish: boolean;
}

/**
 * Lê permissões split (manage/approve/publish) via injection de função
 * `hasPermission`. Evita acoplamento direto com @alamo/auth (peer package).
 * Caller passa `usePermissions().hasPermission` de @alamo/auth.
 *
 * Não é um React hook (não chama nada hook-y) — apenas helper puro.
 */
export function buildPricingPermissions(
  hasPermission: (resource: string, action: string) => boolean,
): PricingPermissions {
  return {
    canManage: hasPermission('pricing', 'manage'),
    canApprove: hasPermission('pricing', 'approve'),
    canPublish: hasPermission('pricing', 'publish'),
  };
}
